// src/ConveChatArea.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { ConveMessage, ConveFile } from './types/conve';
import { useConveStream } from './hooks/useConveStream';
import conveAxios from './api/conveApi';
import './ConveChatArea.css';
import MarkdownRenderer, { extractLongestCodeBlock } from './MarkdownRenderer';

interface Props {
  roomId: number;
  isVisible?: boolean;
  model: string;
  onFilePanelOpen: (file: ConveFile) => void;
  onCodePanelOpen: (code: string, language: string) => void;
  onMessageDone?: () => void;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
}

function getFileIcon(mimeType: string, name: string): string {
  if (mimeType?.startsWith('image/')) return '🖼';
  if (mimeType?.includes('pdf')) return '📕';
  if (mimeType?.includes('word') || name?.endsWith('.doc') || name?.endsWith('.docx')) return '📘';
  if (mimeType?.includes('excel') || name?.endsWith('.xls') || name?.endsWith('.xlsx')) return '📗';
  if (mimeType?.includes('powerpoint') || name?.endsWith('.ppt') || name?.endsWith('.pptx')) return '📙';
  if (mimeType?.includes('hwp')) return '📄';
  return '📄';
}

const ALLOWED_EXTENSIONS = /\.(txt|md|java|kt|py|js|ts|jsx|tsx|json|xml|html|css|scss|sass|less|csv|sql|sh|bash|yaml|yml|pdf|doc|docx|xls|xlsx|ppt|pptx|hwp|c|cpp|cs|go|rs|rb|php|swift|vue|svelte)$/i;

function isAllowedFile(file: File): boolean {
  if (ALLOWED_EXTENSIONS.test(file.name)) return true;
  const allowed = [
    'image/', 'text/', 'application/json', 'application/xml',
    'application/pdf',
    'application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument',
    'application/haansofthwp', 'application/x-hwp',
  ];
  return allowed.some(t => file.type.startsWith(t));
}

const ConveChatArea: React.FC<Props> = ({ roomId, isVisible, model, onFilePanelOpen, onCodePanelOpen, onMessageDone, onStreamStart, onStreamEnd }) => {
  const [messages, setMessages]         = useState<ConveMessage[]>([]);
  const [loading, setLoading]           = useState(false);
  const [streamingMsg, setStreamingMsg] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [charCount, setCharCount]       = useState(0);
  const [isDragging, setIsDragging]     = useState(false);
  const [statusMsg, setStatusMsg]       = useState('');

  const inputRef     = useRef<HTMLTextAreaElement>(null);
  const bottomRef    = useRef<HTMLDivElement>(null);
  const messagesRef  = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const accumRef     = useRef('');
  const initialLoadRef = useRef(false);

  const { streaming, sendMessage, stopStream } = useConveStream();

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = messagesRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    initialLoadRef.current = false;
    try {
      const res = await conveAxios.get(`/rooms/${roomId}/messages`);
      setMessages(res.data.data || []);
      initialLoadRef.current = true;
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    loadMessages();
    const pending = sessionStorage.getItem('conve_pending_msg');
    if (pending) {
      sessionStorage.removeItem('conve_pending_msg');
      setTimeout(() => handleSend(pending, []), 300);
    }
  }, [roomId]);

  const prevVisibleRef = useRef(false);
  useEffect(() => {
    if (isVisible && !prevVisibleRef.current && !streaming) {
      loadMessages();
    }
    prevVisibleRef.current = !!isVisible;
  }, [isVisible]);

  useEffect(() => {
    const shouldFocus = sessionStorage.getItem('conve_focus_input');
    if (shouldFocus) {
      sessionStorage.removeItem('conve_focus_input');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [roomId]);

  useEffect(() => {
    if (!loading && initialLoadRef.current) {
      initialLoadRef.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom('auto');
        });
      });
    }
  }, [loading]);

  useEffect(() => {
    if (streamingMsg) {
      scrollToBottom('auto');
    }
  }, [streamingMsg]);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      requestAnimationFrame(() => scrollToBottom('auto'));
    }
  }, [messages]);

  const handleSend = useCallback(async (text?: string, files?: File[]) => {
    const msgText  = text  ?? inputRef.current?.value.trim() ?? '';
    const msgFiles = files ?? pendingFiles;

    if (!msgText && msgFiles.length === 0) return;
    if (streaming) return;

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.style.height = 'auto';
    }
    setCharCount(0);
    setPendingFiles([]);
    setStatusMsg('');
    accumRef.current = '';

    // 이미지 파일은 FileReader로 base64 미리보기 생성
    const tempFiles = await Promise.all(msgFiles.map((f, i) => {
      return new Promise<ConveFile>((resolve) => {
        if (f.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = (e.target?.result as string)?.split(',')[1] || '';
            resolve({
              id: i,
              messageId: 0,
              originalName: f.name,
              storedName: '',
              fileSize: f.size,
              mimeType: f.type,
              fileType: 'input',
              expiresAt: '',
              isDeleted: 0,
              createdAt: '',
              content: base64,
            });
          };
          reader.readAsDataURL(f);
        } else {
          resolve({
            id: i,
            messageId: 0,
            originalName: f.name,
            storedName: '',
            fileSize: f.size,
            mimeType: f.type,
            fileType: 'input',
            expiresAt: '',
            isDeleted: 0,
            createdAt: '',
          });
        }
      });
    }));

    const tempUserMsg: ConveMessage = {
      id: Date.now(),
      roomId,
      role: 'user',
      content: msgText,
      tokenCount: 0,
      isSummarized: 0,
      createdAt: new Date().toISOString(),
      files: tempFiles,
    };
    setMessages(prev => [...prev, tempUserMsg]);

    onStreamStart?.();
    await sendMessage(
      roomId, msgText, model, msgFiles,
      (chunk) => {
        accumRef.current += chunk;
        setStreamingMsg(accumRef.current);
        setStatusMsg('');
      },
      (messageId) => {
        onStreamEnd?.();
        const finalText = accumRef.current;
        flushSync(() => {
          setStreamingMsg('');
          setStatusMsg('');
          setMessages(prev => [...prev, {
            id: messageId, roomId, role: 'assistant',
            content: finalText, tokenCount: 0, isSummarized: 0,
            createdAt: new Date().toISOString(), files: [],
          }]);
        });
        accumRef.current = '';

        // const longest = extractLongestCodeBlock(finalText);
        // if (longest && longest.language && longest.language.trim() !== '') {
        //   onCodePanelOpen(longest.code, longest.language);
        // }
        onMessageDone?.();

        // 전송 완료 후 메시지 재로드 (서버에서 실제 content 포함 파일 정보 가져오기)
        loadMessages();

        setTimeout(() => inputRef.current?.focus(), 50);
      },
      (errMsg) => {
        onStreamEnd?.();
        setStreamingMsg('');
        setStatusMsg('');
        accumRef.current = '';
        alert(errMsg);
        loadMessages();
        setTimeout(() => inputRef.current?.focus(), 50);
      },
      (status) => setStatusMsg(status),
    );
  }, [roomId, model, pendingFiles, streaming, sendMessage, onCodePanelOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    setCharCount(ta.value.length);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const all = Array.from(e.target.files || []);
    const rejected = all.filter(f => !isAllowedFile(f));
    const accepted = all.filter(f => isAllowedFile(f));
    if (rejected.length > 0) {
      alert(`지원하지 않는 파일 형식입니다:\n${rejected.map(f => f.name).join('\n')}`);
    }
    if (accepted.length > 0) setPendingFiles(prev => [...prev, ...accepted]);
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);

    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (imageItems.length > 0) {
      e.preventDefault();
      imageItems.forEach(item => {
        const file = item.getAsFile();
        if (file) {
          const namedFile = new File([file], `clipboard_${Date.now()}.png`, { type: file.type });
          setPendingFiles(prev => [...prev, namedFile]);
        }
      });
      return;
    }

    // const text = e.clipboardData.getData('text');
    // if (text.length >= 500) {
    //   e.preventDefault();
    //   const blob = new Blob([text], { type: 'text/plain' });
    //   const file = new File([blob], `paste_${Date.now()}.txt`, { type: 'text/plain' });
    //   setPendingFiles(prev => [...prev, file]);
    // }
  };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) {
      alert('파일을 인식할 수 없습니다.\n파일 탐색기에서 드래그해 주세요.');
      return;
    }
    const files = droppedFiles.filter(f => {
      if (!isAllowedFile(f)) { alert(`${f.name}: 지원하지 않는 파일 형식입니다.`); return false; }
      const isImage  = f.type.startsWith('image/');
      const isBinary = !isImage && (
        f.type.includes('pdf') || f.type.includes('officedocument') ||
        f.type.includes('msword') || f.type.includes('ms-excel') ||
        f.type.includes('ms-powerpoint') || f.type.includes('hwp')
      );
      const maxSize = isImage ? 5 * 1024 * 1024 : isBinary ? 10 * 1024 * 1024 : 500 * 1024;
      if (f.size > maxSize) {
        const label = isImage ? '5MB' : isBinary ? '10MB' : '500KB';
        alert(`${f.name}: ${label} 이하만 가능합니다.`);
        return false;
      }
      return true;
    });
    if (files.length > 0) setPendingFiles(prev => [...prev, ...files]);
  };

  return (
    <div
      className={`conve-chat-area ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="conve-drag-overlay">
          <div className="conve-drag-hint">📎 파일을 여기에 놓으세요</div>
        </div>
      )}

      <div className="conve-messages" ref={messagesRef}>
        {loading ? (
          <div className="conve-msg-loading">
            <span className="conve-spinner-sm" />
            대화 불러오는 중...
          </div>
        ) : messages.length === 0 && !streaming ? (
          <div className="conve-msg-empty">메시지를 입력해서 대화를 시작하세요</div>
        ) : (
          messages.map(msg => (
            <MessageItem
              key={msg.id}
              msg={msg}
              onFilePanelOpen={onFilePanelOpen}
              onCodePanelOpen={onCodePanelOpen}
            />
          ))
        )}

        {streaming && (
          <div className="conve-msg-wrap assistant">
            <div className="conve-msg-bubble assistant">
              {streamingMsg ? (
                <>
                  <MarkdownRenderer content={streamingMsg} streaming={true} />
                  <span className="conve-cursor" />
                </>
              ) : statusMsg ? (
                <div className="conve-status-msg">
                  <span className="conve-spinner-sm" />
                  {statusMsg}
                </div>
              ) : (
                <div className="conve-thinking"><span /><span /><span /></div>
              )}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="conve-input-area">
        {pendingFiles.length > 0 && (
          <div className="conve-file-chips">
            {pendingFiles.map((f, i) => (
              <div key={i} className="conve-file-chip">
                <span className="conve-chip-icon">{getFileIcon(f.type, f.name)}</span>
                <span className="conve-chip-name">{f.name}</span>
                <button className="conve-chip-rm" onClick={() => removeFile(i)}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="conve-input-box">
          <textarea
            ref={inputRef}
            className="conve-input-ta"
            placeholder={streaming ? '' : '메시지를 입력하세요... (이미지 붙여넣기 가능)'}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={streaming}
            rows={1}
          />
          <div className="conve-input-toolbar">
            <div className="conve-toolbar-left">
              <button
                className="conve-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming}
                title="파일 첨부"
              >📎</button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.txt,.md,.java,.kt,.py,.js,.ts,.jsx,.tsx,.json,.xml,.html,.css,.scss,.sass,.less,.csv,.sql,.sh,.bash,.yaml,.yml,.c,.cpp,.cs,.go,.rs,.rb,.php,.swift,.vue,.svelte,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
            <div className="conve-toolbar-right">
              <span className={`conve-char-cnt ${charCount > 2000 ? 'warn' : ''}`}>
                {charCount > 0 ? `${charCount} / 2000` : ''}
              </span>
              {streaming ? (
                <button className="conve-send-btn stop" onClick={stopStream} title="중지">■</button>
              ) : (
                <button
                  className={`conve-send-btn ${charCount === 0 && pendingFiles.length === 0 ? 'off' : ''}`}
                  onClick={() => handleSend()}
                  disabled={charCount === 0 && pendingFiles.length === 0}
                >↑</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageItem = React.memo(({ msg, onFilePanelOpen, onCodePanelOpen }: {
  msg: ConveMessage;
  onFilePanelOpen: (file: ConveFile) => void;
  onCodePanelOpen: (code: string, language: string) => void;
}) => {
  const isUser = msg.role === 'user';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(msg.content || '');
  };

  return (
    <div className={`conve-msg-wrap ${isUser ? 'user' : 'assistant'}`}>
      <div className={`conve-msg-bubble ${isUser ? 'user' : 'assistant'}`}>
        {msg.files && msg.files.length > 0 && (
          <div className="conve-msg-files">
            {msg.files.map(file => {
              const isImage   = file.mimeType?.startsWith('image/');
              const isExpired = file.isDeleted === 1;
              const isPending = file.id === 0;

              // 이미지 썸네일
              if (isImage && file.content && !isExpired) {
                return (
                  <div
                    key={file.id || file.originalName}
                    className="conve-img-thumbnail"
                    onClick={() => !isPending && onFilePanelOpen(file)}
                    title={isPending ? '저장 중...' : '이미지 보기'}
                    style={{ cursor: isPending ? 'default' : 'pointer' }}
                  >
                    <img
                      src={`data:${file.mimeType};base64,${file.content}`}
                      alt={file.originalName}
                    />
                    {isPending && <div className="conve-img-pending">저장 중...</div>}
                  </div>
                );
              }

              // 일반 파일 칩
              return (
                <div
                  key={file.id}
                  className={`conve-file-chip-msg ${isExpired ? 'expired' : ''}`}
                  onClick={() => !isExpired && !isPending && onFilePanelOpen(file)}
                  title={isExpired ? '만료된 파일' : isPending ? '저장 중...' : '파일 보기'}
                  style={{ cursor: isPending ? 'default' : 'pointer' }}
                >
                  <span>{getFileIcon(file.mimeType || '', file.originalName)}</span>
                  <span className="conve-chip-name">{file.originalName}</span>
                  {isExpired && <span className="conve-expired-badge">만료</span>}
                </div>
              );
            })}
          </div>
        )}

        {isUser ? (
          <div className="conve-user-text">{msg.content}</div>
        ) : (
          <MarkdownRenderer
            content={msg.content || ''}
            onCodePanelOpen={onCodePanelOpen}
          />
        )}

        {!isUser && (
          <div className="conve-msg-actions">
            <button className="conve-msg-act" onClick={copyToClipboard} title="복사">📋</button>
          </div>
        )}
      </div>
    </div>
  );
});

export default ConveChatArea;